import { SerpTabId } from '@/features/unified-search/SerpTabs.types';
import { SearchState, searchV2, SEARCH_ACTION } from './searchV2Reducer';

type CompaniesAction = Parameters<typeof searchV2>[1];

export type SerpTabCounts = Partial<Record<SerpTabId, number>>;

export type SerpTabQuery = { search: string; query: string };

export type UnifiedSearchState = {
  companies: SearchState;
  companiesRequestKey: string | undefined;
  companiesFailedRequestKey: string | undefined;
  countsBySearch: Record<string, SerpTabCounts>;
  countsKey: string | undefined;
  tabQueries: Partial<Record<SerpTabId, SerpTabQuery>>;
};

export const UNIFIED_SEARCH_ACTION = {
  COMPANIES: 'unifiedSearch/COMPANIES',
  COMPANIES_LOADED: 'unifiedSearch/COMPANIES_LOADED',
  COMPANIES_FAILED: 'unifiedSearch/COMPANIES_FAILED',
  SET_COUNTS: 'unifiedSearch/SET_COUNTS',
  SET_TAB_QUERY: 'unifiedSearch/SET_TAB_QUERY',
  RESET: 'unifiedSearch/RESET',
} as const;

export type UnifiedSearchAction =
  | { type: typeof UNIFIED_SEARCH_ACTION.COMPANIES; payload: CompaniesAction }
  | {
      type: typeof UNIFIED_SEARCH_ACTION.COMPANIES_LOADED;
      payload: { results: Partial<SearchState>; requestKey: string };
    }
  | { type: typeof UNIFIED_SEARCH_ACTION.COMPANIES_FAILED; payload: { requestKey: string } }
  | { type: typeof UNIFIED_SEARCH_ACTION.SET_COUNTS; payload: { counts: SerpTabCounts; countsKey: string } }
  | { type: typeof UNIFIED_SEARCH_ACTION.SET_TAB_QUERY; payload: { tab: SerpTabId; value: SerpTabQuery } }
  | { type: typeof UNIFIED_SEARCH_ACTION.RESET };

export const companiesAction = (action: CompaniesAction): UnifiedSearchAction => ({
  type: UNIFIED_SEARCH_ACTION.COMPANIES,
  payload: action,
});

const initialState: UnifiedSearchState = {
  companies: searchV2(undefined, { type: SEARCH_ACTION.SET_RESET_RESULTS }),
  companiesRequestKey: undefined,
  companiesFailedRequestKey: undefined,
  countsBySearch: {},
  countsKey: undefined,
  tabQueries: {},
};

export function unifiedSearch(
  state: UnifiedSearchState = initialState,
  action: UnifiedSearchAction,
): UnifiedSearchState {
  switch (action.type) {
    case UNIFIED_SEARCH_ACTION.COMPANIES:
      return { ...state, companies: searchV2(state.companies, action.payload) };
    case UNIFIED_SEARCH_ACTION.COMPANIES_LOADED:
      return {
        ...state,
        companies: searchV2(state.companies, { type: SEARCH_ACTION.SET_RESULTS, payload: action.payload.results }),
        companiesRequestKey: action.payload.requestKey,
        companiesFailedRequestKey: undefined,
      };
    case UNIFIED_SEARCH_ACTION.COMPANIES_FAILED:
      return {
        ...state,
        companies: searchV2(state.companies, { type: SEARCH_ACTION.SET_FETCHING, payload: false }),
        companiesFailedRequestKey: action.payload.requestKey,
      };
    case UNIFIED_SEARCH_ACTION.SET_COUNTS: {
      const { counts, countsKey } = action.payload;
      const { [countsKey]: replaced, ...others } = state.countsBySearch;
      return { ...state, countsBySearch: { ...others, [countsKey]: counts }, countsKey };
    }
    case UNIFIED_SEARCH_ACTION.SET_TAB_QUERY:
      return { ...state, tabQueries: { ...state.tabQueries, [action.payload.tab]: action.payload.value } };
    case UNIFIED_SEARCH_ACTION.RESET:
      return initialState;
    default:
      return state;
  }
}
